import { useMutation, useQueryClient } from "react-query";
import { isAxiosError } from "axios";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import { getOrganizationPostApiKey } from "./useGetOrganizationPost";
import { Post } from "src/api/client-api/post/postSchema";
import { extractValidationMessages } from "src/utils/validation";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";

type LockUnlockOptions = {
    post: Post;
    isLocked: boolean;
    forceLock: boolean;
};

type UseLockUnlockPostOptions = {
    onValidationError: (messages: ValidationMessage[]) => void;
    onValidationWarning: (messages: ValidationMessage[]) => void;
};

export const useLockUnlockPost = (options: UseLockUnlockPostOptions) => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: LockUnlockOptions) => {
            const { post, isLocked, forceLock } = options;

            const url = `/api/ssq-posts/${post.id}`;
            const payload = {
                isLocked: !isLocked,
                forceLock: forceLock,
            };

            return await loggedInApiCall.put(url, payload, {
                headers: {
                    Accept: "application/json",
                },
            });
        },
        onSuccess: (_data, options) => {
            const { post } = options;

            const companyId = post.organization?.id;
            const ssqId = post.ssqId;
            const campaignId = post.campaign?.id;

            queryClient.invalidateQueries({ queryKey: [getOrganizationPostApiKey, companyId, ssqId, campaignId] });
        },
        onError: async (error) => {
            snackbar.forceClose();

            if (isAxiosError(error)) {
                const statusCode = error.response?.status;
                if (statusCode === 422) {
                    const violationMessages = extractValidationMessages(error.response?.data);
                    if (violationMessages.length > 0) {
                        options.onValidationError(violationMessages);

                        snackbar.setSnackbarProps({
                            type: "error",
                            message: t("ssq.form.error"),
                        });

                        return;
                    }
                }

                if (statusCode === 409) {
                    const violationMessages = extractValidationMessages(error.response?.data, "warning");
                    if (violationMessages.length > 0) {
                        options.onValidationWarning(violationMessages);

                        return;
                    }
                }

                const isForbidden = statusCode === 401;
                if (isForbidden) {
                    return;
                }
            }

            snackbar.setSnackbarProps({
                type: "error",
                message: t("ssq.form.error"),
            });
        },
    });
};
